/**
 * gtag.jsのd.tsからコピー
 * gtag.js側の定義が流用できなかったのでここで定義
 *
 * see @types/gtag.js/index.d.ts
 */
interface ConfigParams {
  page_title?: string | undefined;
  page_location?: string | undefined;
  page_path?: string | undefined;
  send_page_view?: boolean | undefined;
}

interface CustomParams {
  [key: string]: any;
}

interface ControlParams {
  groups?: string | string[] | undefined;
  send_to?: string | string[] | undefined;
  event_callback?: (() => void) | undefined;
  event_timeout?: number | undefined;
}

type EventNames =
  | "add_payment_info"
  | "add_shipping_info"
  | "add_to_cart"
  | "add_to_wishlist"
  | "begin_checkout"
  | "checkout_progress"
  | "earn_virtual_currency"
  | "exception"
  | "generate_lead"
  | "join_group"
  | "level_end"
  | "level_start"
  | "level_up"
  | "login"
  | "page_view"
  | "post_score"
  | "purchase"
  | "refund"
  | "remove_from_cart"
  | "screen_view"
  | "search"
  | "select_content"
  | "select_item"
  | "select_promotion"
  | "set_checkout_option"
  | "share"
  | "sign_up"
  | "spend_virtual_currency"
  | "tutorial_begin"
  | "tutorial_complete"
  | "unlock_achievement"
  | "timing_complete"
  | "view_cart"
  | "view_item"
  | "view_item_list"
  | "view_promotion"
  | "view_search_results";

interface EventParams {
  checkout_option?: string | undefined;
  checkout_step?: number | undefined;
  content_id?: string | undefined;
  content_type?: string | undefined;
  coupon?: string | undefined;
  currency?: string | undefined;
  description?: string | undefined;
  fatal?: boolean | undefined;
  items?: Item[] | undefined;
  method?: string | undefined;
  number?: string | undefined;
  promotions?: Promotion[] | undefined;
  screen_name?: string | undefined;
  search_term?: string | undefined;
  shipping?: Currency | undefined;
  tax?: Currency | undefined;
  transaction_id?: string | undefined;
  value?: number | undefined;
  event_label?: string | undefined;
  event_category?: string | undefined;
}

type Currency = string | number;

interface Item {
  brand?: string | undefined;
  category?: string | undefined;
  creative_name?: string | undefined;
  creative_slot?: string | undefined;
  id?: string | undefined;
  location_id?: string | undefined;
  name?: string | undefined;
  price?: Currency | undefined;
  quantity?: number | undefined;
}

interface Promotion {
  creative_name?: string | undefined;
  creative_slot?: string | undefined;
  id?: string | undefined;
  name?: string | undefined;
}

const DefaultParams = {
  transport_url:
    location.protocol +
    "//" +
    location.hostname +
    (location.port ? ":" + location.port : ""),
  first_party_collection: true,
} as const;

class AnalyticWrapper {
  private orgName?: string;

  private sendEvent(
    eventName: EventNames | (string & unknown),
    eventParams?: ControlParams | EventParams | CustomParams,
  ) {
    gtag("event", eventName, {
      ...DefaultParams,
      ...eventParams,
    });
  }

  private sendConfig(
    config?: ControlParams | EventParams | ConfigParams | CustomParams,
  ) {
    gtag("config", "G-B2V26YH1SF", {
      ...DefaultParams,
      ...config,
    });
  }

  configUser({ userId, accountType }: { userId: string; accountType: string }) {
    this.sendConfig({
      user_id: userId,
      event_category: accountType,
    });
  }

  configOrg({
    orgName,
    accountType,
  }: {
    orgName: string;
    accountType: string;
  }) {
    this.orgName = orgName;
    this.sendConfig({
      event_label: orgName,
      event_category: accountType,
    });
  }

  downloadGrafferServiceDocument() {
    this.sendEvent("download_graffer_service_document", {
      event_label: this.orgName,
    });
  }

  downloadApplicationPdf() {
    this.sendEvent("download_application_pdf", {
      event_label: this.orgName,
    });
  }

  downloadApplicationAttachedFiles() {
    this.sendEvent("download_application_attached_files", {
      event_label: this.orgName,
    });
  }

  updateProcedure(procObj: { id: string; name: string }) {
    this.sendEvent("update_procedure", {
      event_label: this.orgName,
      proc_id: procObj.id,
      proc_name: procObj.name,
    });
  }

  updateProcedureFormSchema(procObj: { id: string; name: string }) {
    this.sendEvent("update_procedure_form_schema", {
      event_label: this.orgName,
      proc_id: procObj.id,
      proc_name: procObj.name,
    });
  }

  updatePublishedProcedureFormSchema(procObj: { id: string; name: string }) {
    this.sendEvent("update_published_procedure_form_schema", {
      event_label: this.orgName,
      proc_id: procObj.id,
      proc_name: procObj.name,
    });
  }

  procedureDetailPullDownSelect(
    procObj: { id: string; name: string },
    selectedMenu: string,
  ) {
    this.sendEvent("procedure_detail_pull_down_select", {
      event_label: this.orgName,
      proc_id: procObj.id,
      proc_name: procObj.name,
      selected_menu: selectedMenu,
    });
  }

  procedureSearch(searchWord: string, dataLength: number) {
    this.sendEvent("procedure_search", {
      event_label: this.orgName,
      search_word: searchWord,
      search_word_length: searchWord.length,
      search_result_count: dataLength,
    });
  }

  alertWhenFormUnsaved(proc?: { id: string; name: string }) {
    this.sendEvent("alert_when_form_unsaved", {
      event_label: this.orgName,
      proc_id: proc?.id,
      proc_name: proc?.name,
    });
  }

  alertWhenSurveyFormUnsaved(survey?: { id: string; name: string }) {
    this.sendEvent("alert_when_survey_form_unsaved", {
      event_label: this.orgName,
      survey_id: survey?.id,
      survey_name: survey?.name,
    });
  }

  clickNotification({ slug, url }: { slug: string; url: string }) {
    this.sendEvent("click_notification", {
      event_label: this.orgName,
      proc_slug: slug,
      proc_url: url,
    });
  }

  clickHelpFeedBack({
    feedbackType,
    url,
  }: {
    url: string;
    feedbackType: "YES" | "NO";
  }) {
    this.sendEvent("click_help_feedback", {
      event_label: this.orgName,
      proc_url: url,
      feedback_type: feedbackType,
    });
  }

  showNotificationPopup() {
    this.sendEvent("show_notification_popup", {
      event_label: this.orgName,
    });
  }

  helpContentSearch(searchWord: string, dataLength: number) {
    this.sendEvent("help_content_search", {
      event_label: this.orgName,
      search_word: searchWord,
      search_word_length: searchWord.length,
      search_result_count: dataLength,
    });
  }
}

const Analytics = new AnalyticWrapper();
export default Analytics;
